import classNames from 'classnames'
import Icon from 'common/Icon'
import { CHARCOAL_BLACK, DIM_GREY, YELLOW } from 'constants/colors'
import Spinner from 'common/Spinner'
import styles from './index.module.scss'
import { cn } from '@/lib/utils'

type ModalContainerProps = {
  title?: string
  ariaModal?: boolean
  ariaLabel?: string
  subtext?: string
  subtextDark?: boolean
  subtextClassName?: string
  showSubtextIcon?: boolean // This is used in support cancel appointment modal
  backText?: string
  submitText?: string
  submitId?: string
  isSubmitDisabled?: boolean
  isSubmitting?: boolean
  disableParentOverflow?: boolean
  submitClassName?: string
  closeClassName?: string
  showBackIcon?: boolean
  onSubmit?: () => void
  onClose?: () => void
  onCloseIcon?: () => void //This is used to show the cross icon on the top
  closeIconStyle?: React.CSSProperties
  titleClassName?: string
  showBottomBorder?: boolean
  hasSubmitAndCancelButtons?: boolean
  hasGrayHeaderBackground?: boolean
}

const ModalContainer: React.FC<ModalContainerProps> = ({
  children,
  title,
  ariaModal,
  ariaLabel,
  subtext,
  subtextDark = false,
  subtextClassName = '',
  showSubtextIcon = false,
  submitId = '',
  backText = 'Back',
  submitText = 'Apply',
  isSubmitDisabled = false,
  isSubmitting = false,
  disableParentOverflow = false,
  showBackIcon = true,
  submitClassName = '',
  closeClassName = '',
  onSubmit,
  onClose,
  onCloseIcon,
  closeIconStyle,
  titleClassName = '',
  showBottomBorder = true,
  hasSubmitAndCancelButtons = true,
  hasGrayHeaderBackground = true,
}) => {
  return (
    <div
      className={classNames('~relative ~flex ~flex-col ~h-full', {
        '~overflow-auto': !disableParentOverflow,
      })}
      aria-modal={ariaModal}
      role={ariaLabel ? 'dialog' : null}
      aria-label={ariaLabel}
    >
      {onCloseIcon && (
        <button
          type="button"
          className={styles.close_btn}
          style={closeIconStyle}
          onClick={onCloseIcon}
          aria-label="Close modal"
        >
          <Icon icon="lightCross" className="fa-md" fill={DIM_GREY} />
        </button>
      )}
      {(title || subtext) && (
        <div
          className={
            hasGrayHeaderBackground
              ? styles.modal_header_container
              : styles.modal_header_container_white
          }
        >
          <div className={`${styles.modal_header_title} ${titleClassName}`}>
            {title}
          </div>
          {subtext && (
            <div
              className={classNames(
                'fs-14',
                {
                  'secondary-text': !subtextDark,
                  [styles.modal_subtext]: subtextDark,
                },
                subtextClassName
              )}
            >
              {showSubtextIcon && (
                <>
                  <Icon icon="warning" className="fa-lg" fill={YELLOW} />{' '}
                </>
              )}
              {subtext}
            </div>
          )}
        </div>
      )}
      <div
        className={classNames('~flex-1 ~mr-2', {
          '~overflow-auto': !disableParentOverflow,
        })}
      >
        {children}
      </div>
      {(showBottomBorder || hasSubmitAndCancelButtons) && (
        <div className="~flex ~flex-col-reverse sm:~flex-row ~justify-end sm:~px-20 md:~px-4 ~py-4 ~relative">
          {showBottomBorder && (
            <div className="~absolute ~top-0 ~left-0 ~right-0 ~bg-light-grey height-1" />
          )}
          {onClose && hasSubmitAndCancelButtons && (
            <button
              type="button"
              onClick={onClose}
              className={`${closeClassName} ${styles.modal_action_button} flex-center font-weight-medium fs-16`}
            >
              {showBackIcon && (
                <Icon
                  icon="back"
                  fill={CHARCOAL_BLACK}
                  className="fa-md ~mr-2"
                />
              )}
              {backText}
            </button>
          )}
          {onSubmit && hasSubmitAndCancelButtons && (
            <button
              id={submitId}
              type="button"
              disabled={isSubmitDisabled || isSubmitting}
              onClick={onSubmit}
              className={cn(
                '~inline-block ~align-middle ~leading-normal ~bg-[#0d85f4] ~text-white ~text-center ~rounded-[6px] ~border ~border-[#0d85f4] ~cursor-pointer ~select-none ~transition-[color,background-color,border-color,box-shadow] ~duration-150 ~ease-in-out ~px-3 hover:~bg-[#0b74d3] hover:~border-[#0b74d3] active:~bg-[#0b6bbf] active:~border-[#0a63b3] focus:~bg-[#0c77d9] focus:~border-[#0b74d3] focus:~outline-none focus:~shadow-[0_0_0_0.2rem_rgba(58,176,195,0.5)] disabled:~bg-[#cccccc] disabled:~border-[#cccccc] disabled:~opacity-65 disabled:~pointer-events-none',
                submitClassName,
                styles.modal_action_button,
                'fs-16 font-weight-input ~mt-1 sm:~mt-0 ~mb-4 sm:~mb-0 ~py-2'
              )}
            >
              {isSubmitting ? <Spinner /> : submitText}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default ModalContainer
